import React, { useState, useRef, useEffect } from 'react';

interface TailwindSelectProps {
  options: { id: string; text: string; disabled?: boolean }[];
  value?: string;
  onChange: (value: string) => void;
  placeholder?: string;
  clearable?: boolean;
  searchable?: boolean;
  className?: string;
  disabled?: boolean;
  onBlur?: (event: React.FocusEvent) => void;
  onFocus?: (event: React.FocusEvent) => void;
  onOpenDropDown?: () => void; // Nueva prop
}

const TailwindSelect: React.FC<TailwindSelectProps> = ({
  options,
  value,
  onChange,
  placeholder,
  clearable = true,
  searchable = true,
  className = '',
  disabled = false,
  onBlur,
  onFocus,
  onOpenDropDown
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const hasOpenedRef = useRef(false); // Nuevo ref para controlar si se ha abierto

  const filteredOptions = searchable
    ? options.filter(option => (option.text != null ? option.text.toLowerCase().includes(search.toLowerCase()) : false))
    : options;

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        hasOpenedRef.current = false; // Reset cuando se cierra
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleOpen = () => {
    if (!disabled) {
      if (!isOpen && !hasOpenedRef.current && onOpenDropDown) {
        onOpenDropDown();
        hasOpenedRef.current = true;
      }
      setIsOpen(true);
    }
  };

  const handleSelect = (option: { id: string; text: string; disabled?: boolean }) => {
    //if (option.disabled) return;
    onChange(option.id);
    setIsOpen(false);
    setSearch('');
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange('');
    setSearch('');
  };

  const selectedOption = options.find(opt => opt.id === value);

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <div
        className={`
          relative w-full cursor-pointer rounded-md border bg-white py-3 pl-3 pr-10 text-left shadow-sm
          ${isOpen ? 'border-blue-500 ring-1 ring-blue-500' : 'border-gray-300'}
          ${disabled ? 'cursor-not-allowed bg-gray-50 text-gray-500' : ''}
        `}
        onClick={handleOpen}
        onFocus={onFocus}
        onBlur={onBlur}
      >
        <span className="block truncate">
          {selectedOption ? selectedOption.text : <span className="text-gray-400">{placeholder}</span>}
        </span>
        {clearable && value && !disabled && (
          <button onClick={handleClear} className="absolute inset-y-0 right-7 flex items-center pr-2 text-gray-400 hover:text-gray-600">
            ×
          </button>
        )}
        <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
          <svg className="h-5 w-5 text-gray-400" viewBox="0 0 20 20" fill="currentColor">
            <path
              fillRule="evenodd"
              d="M10 3a1 1 0 01.707.293l3 3a1 1 0 01-1.414 1.414L10 5.414 7.707 7.707a1 1 0 01-1.414-1.414l3-3A1 1 0 0110 3zm-3.707 9.293a1 1 0 011.414 0L10 14.586l2.293-2.293a1 1 0 011.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z"
              clipRule="evenodd"
            />
          </svg>
        </span>
      </div>

      {isOpen && !disabled && (
        <div className="absolute z-10 mt-1 w-full rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5">
          {searchable && (
            <div className="px-3 py-2 border-b border-gray-200">
              <input
                type="text"
                className="w-full rounded-md border-0 focus:ring-0 focus:outline-none sm:text-smm"
                placeholder="Search..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                onClick={e => e.stopPropagation()}
                autoFocus
              />
            </div>
          )}

          <div className="max-h-60 overflow-auto">
            {filteredOptions.map(option => (
              <div
                key={option.id}
                className={`px-4 py-2 cursor-pointer ${
                  option.disabled
                    ? 'cursor-default text-gray-600'
                    : `hover:bg-gray-100
                  ${option.id === value ? 'bg-blue-50 text-blue-700' : 'text-gray-900'}`
                }`}
                onClick={() => handleSelect(option)}
              >
                {option.text}
              </div>
            ))}
            {filteredOptions.length === 0 && <div className="px-4 py-2 text-sm text-gray-500 text-center">No options found</div>}
          </div>
        </div>
      )}
    </div>
  );
};

export default TailwindSelect;
